<template>
  <div class='sitemap-issue-body'>
    <div class="description-block" :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container description" :class='{closed: !isShowAll}'>
        <div class="finding-summary">
          <div class="data-sub-title">{{findingSummary.title}}</div>
          <div class="text-container description">{{findingSummary.data}}</div>
        </div>
        <div class="details">
          <div class="data-sub-title">{{$t('sitemap.issue.popup.details')}}</div>
          <div class="feature-identified">
            <div class='details-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
              <div class="details-option-container">
                <div class='text-container details-title'>{{featureIdentified.title}}</div>
                <div class='text-container'>{{featureIdentified.data}}</div>
              </div>
            </div>
          </div>
          <div class="url-path">
            <div class='details-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
              <div class="details-option-container">
                <div class='text-container details-title'>{{urlPath.title}}</div>
                <div class='text-container url-path' v-for="(item, index) in urlPath.data" :key="index">
                  <a :href='item' target="_blank"> {{item}} </a>
                </div>
              </div>
            </div>
          </div>
          <div class="sitemap-status">
            <div class='details-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
              <div class="details-option-container">
                <div class='text-container details-title'>{{sitemapStatus.title}}</div>
                <div class='text-container'>{{sitemapStatus.data}}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="security-risk">
          <div class="data-sub-title">{{securityRisk.title}}</div>
          <div class="text-container security-risk-description">{{securityRisk.subtitle}}</div>
          <div v-for='(title, key) in Object.keys(securityRisk.data)' :key='key'>
            <div class='security-risk-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
              <div class="security-risk-option-container">
                <div class='text-container security-risk-title'>{{$t(`sitemap.issue.popup.${title}`)}}</div>
                <div class='text-container'>{{securityRisk.data[title]}}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="potential-impacts">
          <div class="data-sub-title">{{potentialImpacts.title}}</div>
          <div v-for='(item, key) in potentialImpacts.data' :key='key'>
            <div class='potential-impacts-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
              <div class="potential-impacts-option-container">
                <div class='text-container'>{{item}}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <span class='hide-description-btn' @click.stop="isShowAll = !isShowAll">
          {{ captionOfDescriptionToggleButton }}
      </span>
    </div>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.asset.title')}}</div>
      <v-badge
        tile
        overlap
        :icon="badgeIcon"
        :content="badgeContent"
        class="manual-config-badge"
        :class="{'copied-badge': displayedCopiedBadge}"
        offset-y='4px'
        :value="displayedCopiedBadge"
      >
        <div class="text-container asset-data-text" @click='copyAsset'>{{asset}}</div>
      </v-badge>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <span class="mitigation-block" v-if='mitigation'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div class="mitigation-section-container" v-for='(mitigationTitle, index) in mitigationTitles' :key='index'>
          <div class="data-sub-title">{{$t(`sitemap.issue.popup.${mitigationTitle}`)}}</div>
          <div v-for='(mitigation, key) in mitigationData[index]' :key='key'>
             <div class='mitigation-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
               <div class="mitigation-option-container">
                <div class='text-container'>{{mitigation}}</div>
               </div>
            </div>
          </div>
      </div>
    </span>
  </div>
</template>

<script>

export default {
  name: 'SitemapIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      displayedCopiedBadge: false,
      isShowAll: false,
    };
  },
  computed: {
    tld() {
      return this.issue.tld;
    },
    asset() {
      return this.issue.asset.replace(/__/g, '.');
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.issue.description;
    },
    findingSummary() {
      return {
        title: this.$t('sitemap.issue.popup.finding.summary'),
        data: this.description.findingSummary,
      };
    },
    details() {
      return this.description.details;
    },
    featureIdentified() {
      return {
        title: this.$t('sitemap.issue.popup.featureIdentified'),
        data: this.details.featureIdentified,
      };
    },
    urlPath() {
      return {
        title: this.$t('sitemap.issue.popup.urlPath'),
        data: this.details.urlPath,
      };
    },
    sitemapStatus() {
      return {
        title: this.$t('sitemap.issue.popup.sitemapStatus'),
        data: this.details.sitemapStatus,
      };
    },
    securityRisk() {
      return {
        title: this.$t('sitemap.issue.popup.security.risk'),
        subtitle: this.description.securityRisk.title,
        data: this.description.securityRisk.data,
      };
    },
    potentialImpacts() {
      return {
        title: this.$t('sitemap.issue.popup.potential.impacts'),
        data: this.description.potentialImpacts,
      };
    },
    mitigation() {
      return this.metaData.mitigation;
    },
    mitigationTitles() {
      return Object.keys(this.mitigation);
    },
    mitigationData() {
      return Object.values(this.mitigation);
    },
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
    captionOfDescriptionToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
  },
  methods: {
    async copyAsset() {
      const data = this.asset;
      await navigator.clipboard.writeText(data);
      this.displayedCopiedBadge = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge = false;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.sitemap-issue-body {
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(650);
    margin-right: $space-4;
  }
  .description-block {
    display: block;
    width: rem(650);
    margin-bottom: $space-5;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      &.closed {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-height: 1.2em;
        height: 3.6em;
      }
    }
    .finding-summary {
      margin-top: rem(15);
    }
    .details {
      margin-top: rem(15);
      .details-option {
        display: flex;
        margin-bottom: $space-2;
        .details-option-container {
          display: inline-block;
          padding-left: $space-2;
          .details-title {
            font-weight: bold;
            margin-bottom: rem(5);
          }
          .url-path {
            margin-bottom: rem(7);
          }
        }
      }
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
    .security-risk {
      margin-top: rem(15);
      .security-risk-description {
        margin-bottom: $space-2;
      }
      .security-risk-option {
        display: flex;
        margin-bottom: $space-2;
        .security-risk-option-container {
          display: inline-block;
          padding-left: $space-2;
          .security-risk-title {
            font-weight: bold;
            margin-bottom: rem(5);
          }
        }
      }
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
    .potential-impacts {
      margin-top: rem(15);
      .potential-impacts-option {
        display: flex;
        margin-bottom: $space-2;
        .potential-impacts-option-container {
          display: inline-block;
          padding-left: $space-2;
        }
      }
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
  }
  .feature-block {
    display: block;
    padding-top: $space-5;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .url-option {
      display: flex;
      .url-option-container {
        display: inline-block;
        padding-left: $space-2;
      }
    }
    .feature-section-container {
      padding-top: $space-2;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
    .description {
      white-space: pre-line;
    }
  }
  .mitigation-block {
    display: block;
    padding-top: $space-5;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
      .mitigation-option-container {
        display: inline-block;
        padding-left: $space-2;
      }
    }
    .mitigation-section-container {
      padding-top: $space-2;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
    .description {
      white-space: pre-line;
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .asset-block {
    margin-bottom: $space-5;
    width: rem(650);
    .manual-config-badge {
      max-width: 100%;
      line-height: 1.5;
      &:hover {
        cursor: pointer;
      }
      .asset-data-text {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .copied {
        text-transform: capitalize;
        color: var(--brand-primary);
      }
    }
  }
  .tld-block {
    margin-bottom: $space-2;
  }
  .description {
    white-space: pre-line;
  }
  .hide-description-btn {
    color: var(--brand-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    letter-spacing: 0;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.sitemap-issue-body {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge {
        background: transparent;
        border: none;
        color: var(--brand-primary);
        .v-icon {
          font-size: rem(16);
          transform: rotate(180deg);
        }
      }
    }
    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge {
          text-transform: capitalize;
          background: transparent;
          border: none;
          color: var(--brand-primary);
          right: rem(20);
          inset: auto auto calc(100% - #{rem(1)}) calc(100% - #{rem(30)});
        }
      }
    }
  }
}
</style>
